/**
 * The game page's query, and the one place it is read (P1.9-T143). The author's rule, 2026-10-07: "yeah all the
 * query based parameters should only be a dev/work locally. none of those should work on prod hey?" Every parameter
 * the page has is a developer's or a test rig's tool, so a page reads its query as written only when it is a
 * development page (`isDevelopmentPage`, `endpoint.ts`): one the dev server would serve at its host, told by the
 * address in the address bar and the machine name the page's own `config.json` carries. Nothing in a link decides
 * that: no parameter, and no world a parameter names. On any other page, the published one and a page reached through
 * a tunnel among them, each parameter is dropped here before anything reads it, so the page behaves as if the link
 * carried none.
 *
 * A parameter a published page truly needs is named in `PRODUCTION_PARAMETERS` with the validation its value must
 * pass and why it is safe to read from a link. The list is empty: a published page needs none today. A token that
 * signs a player in is never such a parameter. The links account mail carries (a password reset, an email
 * confirmation; ADR-092 decision 8) ride in the address's fragment, not its query, as exactly `#reset=<token>` or
 * `#verify=<token>` (`readMailFragment`, `odyssey-shared`): single-use tokens that prove nothing but themselves and
 * reach no server log. The fragment is read only in this module, through `readMailFragment`, and nothing else is read
 * from it: this is the only file of the page `tests/page-query-reads.test.ts` lets read `location.search` or
 * `location.hash`.
 *
 * Two parameters keep a narrower rule of their own on top of this one, since each decides where a player's
 * credentials or words go: `?server=` and `?feedback=` are honoured only on `localhost` and `127.0.0.1`
 * (`isLocalPage`), and `?look=` only at a literal local address (`trial-gate.ts`).
 */

/**
 * Every parameter the game page reads, each a development parameter: the Phase 0 dev token the rigs and local play
 * join with (`token`), the absolute zoom (`zoom`), the diagnostic strip (`diag`), the world, its pointer and where its
 * files are fetched from (`world`, `pointer`, `base`), the gateway (`server`), an artificial delay (`latency`), the
 * feedback route (`feedback`), the benchmark mode with its label and render override (`bench`, `label`, `render`), the
 * trial look and its slots (`look`, `skin`, `outfit`, `cloak`, `face`, `hair`, `hat`, `weapon`, `shield`) and the
 * composed crowd (`looks`), the pause held in a map transfer (`handoffDelay`, milliseconds) and the weather forced for a look (`lights=dev` and `light=off`, the light layer's developer lights and switch, ADR-141's amendment of 2026-10-31; `weather`: clear, rain or storm; `wet` and `dry`: the ground held still at a point of the soak; `puddlefx`: off, a or b, to compare the effects at the feet of what stands in a puddle; the page runs B at Full, A at Reduced; `wind`: 0 to 3, optionally `:left` or `:right`; `clouds`: fair or cloudy, the cloud shadows). `tests/page-query-reads.test.ts` fails when the page asks its query for a name that is
 * neither here nor in `PRODUCTION_PARAMETERS`.
 */
export const DEVELOPMENT_PARAMETERS: readonly string[] = ["token", "zoom", "diag", "world", "pointer", "base", "server", "latency", "feedback", "bench", "label", "render", "look", "skin", "outfit", "cloak", "face", "hair", "hat", "weapon", "shield", "looks", "handoffDelay", "weather", "wet", "dry", "puddlefx", "lights", "light", "wind", "clouds", "drenched"];

/**
 * A parameter a page reads whether or not it is a development page.
 */
export interface AllowedParameter {
  /** The parameter's name, exactly as a link writes it. */
  readonly name: string;
  /**
   * Whether a value is one the page may read. A value that fails is dropped as if the link had not carried it.
   *
   * @param value The decoded value.
   * @returns True for a well-formed value.
   */
  readonly valid: (value: string) => boolean;
  /** What needs it on a published page and why a link carrying it can do a player no harm. */
  readonly why: string;
}

/**
 * The parameters a published page reads: none. An entry is added only with a validation of its value and the reason
 * a crafted link carrying it is harmless, and never for a token that signs a player in.
 */
export const PRODUCTION_PARAMETERS: readonly AllowedParameter[] = [];

/**
 * The query a page may read.
 *
 * @param search The address's query as `location.search` gives it, with or without its question mark.
 * @param development Whether the page is a development page (`isDevelopmentPage`).
 * @param allowed The parameters read on any page, `PRODUCTION_PARAMETERS` unless a test gives its own.
 * @returns On a development page the whole query as written. On any other page a query holding only each allowed
 *   parameter that the link carries exactly once with a value its validation passes.
 * @throws RangeError when an allowed entry carries a development parameter's name, since the two lists would then
 *   disagree about what a published page reads.
 * @example
 * pageQuery("?base=https://elsewhere.example&bench=1", false).toString(); // ""
 * pageQuery("?world=stress&bench=1", true).get("bench"); // "1"
 */
export function pageQuery(search: string, development: boolean, allowed: readonly AllowedParameter[] = PRODUCTION_PARAMETERS): URLSearchParams {
  const clash = allowed.find((entry) => DEVELOPMENT_PARAMETERS.includes(entry.name));
  if (clash !== undefined) {
    throw new RangeError(`?${clash.name}= is a development parameter and cannot be allowed on a published page`);
  }
  const written = new URLSearchParams(search);
  if (development) {
    return written;
  }
  const kept = new URLSearchParams();
  for (const entry of allowed) {
    const values = written.getAll(entry.name);
    const value = values[0];
    if (values.length === 1 && value !== undefined && entry.valid(value)) {
      kept.set(entry.name, value);
    }
  }
  return kept;
}

/**
 * Reads the page's own query.
 *
 * @param development Whether the page is a development page (`isDevelopmentPage`).
 * @returns The query the page may read (`pageQuery`).
 */
export function readPageQuery(development: boolean): URLSearchParams {
  return pageQuery(window.location.search, development);
}
